/**
 * Money representation shared across the rewards platform.
 *
 * Amounts are integer minor units (e.g. cents) to avoid floating point drift,
 * matching the backend `RewardMoney { amountMinor: Long!, currency: String! }`
 * type. Currency is an ISO-4217 alphabetic code. The number of minor units per
 * major unit comes from the runtime's Intl currency data (2 for MXN/USD, 0 for
 * CLP/JPY, 3 for KWD, ...), never from a hardcoded table.
 */
export interface IMoney {
  /** Integer amount in the currency's minor unit (cents for USD/MXN, pesos for CLP). */
  amountMinor: number;
  /** ISO-4217 currency code, e.g. "USD", "MXN". */
  currency: string;
}

/** The GraphQL `RewardMoney` type. Alias of {@link IMoney}. */
export type RewardMoney = IMoney;

/** Locale used by every rewards formatter when the caller does not pass one. */
export const DEFAULT_REWARDS_LOCALE = "es-MX";

/** Fraction digits assumed when the runtime has no data for a currency. */
const FALLBACK_FRACTION_DIGITS = 2;

/** Locale used only to read currency metadata (fraction digits are locale-independent). */
const METADATA_LOCALE = "en-US";

const fractionDigitsCache = new Map<string, number>();

/**
 * Number of fraction digits the currency uses (ISO-4217 minor unit exponent),
 * read from Intl. Unknown / invalid currency codes fall back to 2.
 */
export function currencyFractionDigits(currency: string): number {
  const code = currency.toUpperCase();
  const cached = fractionDigitsCache.get(code);
  if (cached !== undefined) return cached;

  let digits = FALLBACK_FRACTION_DIGITS;
  try {
    const resolved = new Intl.NumberFormat(METADATA_LOCALE, {
      style: "currency",
      currency: code,
    }).resolvedOptions();
    digits = resolved.maximumFractionDigits ?? FALLBACK_FRACTION_DIGITS;
  } catch {
    digits = FALLBACK_FRACTION_DIGITS;
  }
  fractionDigitsCache.set(code, digits);
  return digits;
}

/** Returns the minor-unit divisor for a currency (100 for MXN, 1 for CLP). */
export function minorUnitsFor(currency: string): number {
  return 10 ** currencyFractionDigits(currency);
}

/** Builds an {@link IMoney} from a major-unit decimal amount. */
export function moneyFromMajor(amountMajor: number, currency: string): IMoney {
  return {
    amountMinor: Math.round(amountMajor * minorUnitsFor(currency)),
    currency,
  };
}

/** Converts an {@link IMoney} to its major-unit decimal amount. */
export function moneyToMajor(money: IMoney): number {
  return money.amountMinor / minorUnitsFor(money.currency);
}

/**
 * Formats money for display using Intl with the currency's default fraction
 * digits. Falls back to `"<amount> <CODE>"` (still with the currency's own
 * fraction digits) if the runtime rejects the locale or currency.
 */
export function formatMoney(money: IMoney, locale: string = DEFAULT_REWARDS_LOCALE): string {
  const major = moneyToMajor(money);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: money.currency,
    }).format(major);
  } catch {
    return `${major.toFixed(currencyFractionDigits(money.currency))} ${money.currency}`;
  }
}
